import { forwardRef, useEffect, useImperativeHandle, useState } from 'react'
import { Button, InputNumber, message } from 'antd'
import * as Cesium from '@cesiumgs/cesium-analytics'
import type { Viewer } from '@cesiumgs/cesium-analytics'
import { Cartesian3, Cartographic, ConstantProperty, Entity, Math } from '@cesiumgs/cesium-analytics'
import Draw from '@/components/Cesium/Draw/Draw'
import type DrawAction from '@/components/Cesium/Draw/DrawAction'
import { DrawType } from '@/components/Cesium/Draw/DrawType'
import { DrawEventType } from '@/components/Cesium/Draw/DrawEventType'

import { queryaddress } from '@/service/rightNavCtrl'
import './index.less'

const ENTITY_ID = 'skyline-trackpoint'
const prefix = 'skyline-box'
let drawTool: Draw

const SkylineAnalysis = forwardRef((props, ref) => {
  const [inputValue, setInputValue] = useState(70)
  const [pointPosition, setPointPosition] = useState<any>(null)
  const [messageApi, contextHolder] = message.useMessage()
  const [postProcessStages, setPostProcessStages] = useState<any>(null)
  const [disabled, setDisabled] = useState(true)
  const [disabled2, setDisabled2] = useState(true)

  useImperativeHandle(ref, () => ({
    handleClearAll,
  }))
  useEffect(() => {
    return () => {
      handleClearAll()
    }
  }, [])

  const inputChange = (value: number | null) => {
    setInputValue(value || 0)
  }
  const handleDrawPoint = () => {
    drawTool = new Draw(window.cviewer)
    const action: DrawAction = drawTool.create(DrawType.Point, {
      useDefaultEntity: true,
    })
    action.on(DrawEventType.VertexAdd, (evt) => {
      action.destroy()
      const ellipsoid = window.cviewer.scene.globe.ellipsoid
      const newlnglatArr: any = []
      evt.postions.forEach((item: any) => {
        const cartographic = ellipsoid.cartesianToCartographic(item)
        const lat = Math.toDegrees(cartographic.latitude)
        const lng = Math.toDegrees(cartographic.longitude)
        newlnglatArr.push([lng, lat, cartographic.height])
      })
      queryaddress({ lat: newlnglatArr[0][1], lng: newlnglatArr[0][0] }).then((res: any) => {
        if (res.returnCode === '200') {
          setPointPosition(evt.postions)
          setDisabled(false)
        }
      })
    })
  }
  const handleClearAll = () => {
    drawTool?.reset()
    setDisabled(true)
    setDisabled2(true)
    setInputValue(70)
    ;(window.cviewer as Viewer).entities.removeById(ENTITY_ID)
    window.cviewer.scene.screenSpaceCameraController.enableTranslate = true
    window.cviewer.scene.screenSpaceCameraController.enableZoom = true
    window.cviewer.scene.postProcessStages.removeAll()
    postProcessStages?.destroy()
  }
  const handleAnalysis = () => {
    if (!pointPosition) {
      messageApi.warning('place Draw point')
      return false
    }
    const cartographic = Cartographic.fromCartesian(pointPosition[0])
    const lat = Math.toDegrees(cartographic.latitude).toFixed(4)
    const lng = Math.toDegrees(cartographic.longitude).toFixed(4)
    const height = inputValue
    const totalheight = Number(height) + Number(cartographic.height)
    ;(window.cviewer as Viewer).entities.removeById(ENTITY_ID)
    const entity: Entity = (window.cviewer as Viewer).entities.add(
      new Entity({
        id: ENTITY_ID,
        position: Cartesian3.fromDegrees(Number(lng), Number(lat), Number(totalheight)),
        viewFrom: new ConstantProperty(new Cesium.Cartesian3(0.01, 10, 0)),
        show: false,
      }),
    )

    window.cviewer.camera.flyTo({
      destination: Cesium.Cartesian3.fromDegrees(Number(lng), Number(lat), Number(totalheight)),

      orientation: {
        heading: Cesium.Math.toRadians(0), // east, default value is 0.0 (north)left right
        pitch: Cesium.Math.toRadians(5), // default value (looking down)up down
        roll: 0, // default value
      },
      complete: () => {
        window.cviewer.trackedEntity = entity
        window.cviewer.scene.screenSpaceCameraController.enableTranslate = false
        window.cviewer.scene.screenSpaceCameraController.enableZoom = false
        window.cviewer.scene.postProcessStages.removeAll()
        const edgeDetection = Cesium.PostProcessStageLibrary.createEdgeDetectionStage()
        const postProccessStage = new Cesium.PostProcessStage({
          name: 'czm_skyline0',
          fragmentShader:
            'uniform sampler2D colorTexture;'
            + 'uniform sampler2D depthTexture;'
            + 'varying vec2 v_textureCoordinates;'
            + 'void main(void)'
            + '{'
            + 'float depth = czm_readDepth(depthTexture, v_textureCoordinates);'
            + 'vec4 color = texture2D(colorTexture, v_textureCoordinates);'
            + 'if(depth<1.0 - 0.000001){'
            + 'gl_FragColor = color;'
            + '}'
            + 'else{'
            + 'gl_FragColor = vec4(1.0,0.0,0.0,1.0);'
            + '}'
            + '}',
        })

        const postProccessStage1 = new Cesium.PostProcessStage({
          name: 'czm_skyline1',
          fragmentShader:
            'uniform sampler2D colorTexture;'
            + 'uniform sampler2D redTexture;'
            + 'uniform sampler2D silhouetteTexture;'
            + 'varying vec2 v_textureCoordinates;'
            + 'void main(void)'
            + '{'
            + 'vec4 redcolor=texture2D(redTexture, v_textureCoordinates);'
            + 'vec4 silhouetteColor = texture2D(silhouetteTexture, v_textureCoordinates);'
            + 'vec4 color = texture2D(colorTexture, v_textureCoordinates);'
            + 'if(redcolor.r == 1.0){'
            + 'gl_FragColor = mix(color, vec4(1.0,0.0,0.0,1.0), silhouetteColor.a);'
            + '}'
            + 'else{'
            + 'gl_FragColor = color;'
            + '}'
            + '}',
          uniforms: {
            redTexture: postProccessStage.name,
            silhouetteTexture: edgeDetection.name,
          },
        })
        const postProccessStage2 = new Cesium.PostProcessStageComposite({
          name: 'czm_skyline',
          stages: [edgeDetection, postProccessStage, postProccessStage1],
          inputPreviousStageTexture: false,
          uniforms: edgeDetection.uniforms,
        })
        window.cviewer.scene.postProcessStages.add(postProccessStage2)
        setPostProcessStages(postProccessStage2)
      },
    })
    setDisabled2(false)
  }
  return (
    <>
      {contextHolder}
      <div className={`${prefix}`}>
        <div className={`${prefix}-txt`}>
          시점 지정
          <span>* 시점을 지도에서 선택하세요</span>
        </div>
        <div className={`${prefix}-btn`}>
          <Button onClick={handleDrawPoint} className={`${prefix}-btn-sure`}>
            시점 지정
          </Button>
          <Button disabled={disabled2} onClick={handleClearAll} className={`${prefix}-btn-cancel`}>
            지정 취소
          </Button>
        </div>
        <div className={`${prefix}-height`}>
          <strong className={`${prefix}-height-title`}>시점 높이</strong>
          <div className={`${prefix}-height-input`}>
            <InputNumber value={inputValue} onChange={inputChange} />
            <span>m</span>
          </div>
        </div>
        <div className={`${prefix}-bottom`}>
          <Button onClick={handleAnalysis} disabled={disabled}>분석 실행</Button>
        </div>
      </div>
    </>
  )
})

export default SkylineAnalysis
